// R1c — Learn hub. Tour launcher + cheat sheets + placeholder training paths.
// Truthful: video assets and cert paths are labeled "Coming in R6" — no fakes.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AdminAppShell } from "../design-system/shells/AdminAppShell";
import StatusChip from "../components/StatusChip";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { usePortfolioFeature } from "../lib/portfolio";
import { ARCHITECT_ROLES, FIELD_ROLES, FINANCE_ROLES, usePhaseFeature } from "../lib/phaseFeatures";
import { RotateCcw, BookOpen, Keyboard, Award, Play, Compass, ScrollText } from "lucide-react";

const CHEAT_SHEETS = [
  {
    title: "ERF logo as back navigation",
    icon: BookOpen,
    testId: "learn-sheet-erf-back-logo",
    items: [
      { keys: "ERF logo", action: "Click the ERF logo in the top-left of any admin surface to go back one step — the same way the browser's back button works. Works identically on phone, tablet, and desktop." },
      { keys: "Enter / Space", action: "When the ERF logo is keyboard-focused, press Enter or Space to go back — accessible-by-default." },
      { keys: "History aware", action: "If you came from another in-site page, you return there. If not, you move up one level in admin navigation (for example, /admin/integrations → /admin)." },
      { keys: "Alt + ←", action: "Keyboard shortcut equivalent — still works everywhere inside the admin shell." },
      { keys: "Phone tap target", action: "The ERF logo hit area is at least 44×44 CSS pixels on phones — comfortable to tap with a thumb; no need to zoom." },
    ],
  },
  {
    title: "Using ConstructionOS on your phone",
    icon: BookOpen,
    testId: "learn-sheet-phone-usage",
    items: [
      { keys: "Card layout", action: "On phones, every admin card stacks its status badge beneath the title row so provider names like 'Microsoft 365' always render horizontally — not character-by-character." },
      { keys: "Primary actions", action: "Primary buttons (Connect, Verify, Edit) stack vertically full-width with 44 pixel tap targets so you can act with one thumb." },
      { keys: "Secondary actions", action: "Secondary buttons like 'Edit credentials' sit below the primary action and are the only way to reach the credential drawer — the primary CTA always takes you to the real workflow." },
      { keys: "No zooming required", action: "Readable at 100% browser zoom. If something looks cramped, report it — do not pinch-zoom as a workaround." },
      { keys: "Microsoft 365 Connect", action: "On iPhone/iPad Safari, the Microsoft 365 Connect button is a native link that opens Microsoft's consent screen at the top of the browser (not inside a frame). Tap it directly, do not long-press." },
    ],
  },
  {
    title: "Keyboard shortcuts",
    icon: Keyboard,
    items: [
      { keys: "Alt + ←", action: "Go back to previous surface" },
      { keys: "Backspace", action: "Go back (when nothing is focused)" },
      { keys: "Tab", action: "Move focus to next control" },
      { keys: "Shift + Tab", action: "Move focus to previous control" },
      { keys: "Enter / Space", action: "Activate focused button or expandable row" },
      { keys: "Esc", action: "Close dialog or dropdown" },
    ],
  },
  {
    title: "Common workflows",
    icon: Compass,
    items: [
      { keys: "Lead → Opp", action: "In /crm/leads press Convert to auto-create Opportunity + Account + Contact" },
      { keys: "Estimate → Proposal", action: "Approved estimate is required before drafting proposal in /crm/proposals" },
      { keys: "Proposal → Contract", action: "Accepted proposal unlocks Contract creation in /crm/contracts" },
      { keys: "Contract → Deposit", action: "Fully-signed contract lets you request a deposit in /crm/deposits" },
      { keys: "Deposit → Paid", action: "Manual mark-paid uses authority band + audit ledger; receipt upload optional" },
    ],
  },
];

const TRAINING_TILES = [
  { title: "Owner Command Center", role: "OWNER", desc: "Where every operator invariant lives and how to read the truth tiles." },
  { title: "People & Access", role: "OWNER · ADMIN", desc: "Add users, seed roles, wire the authority matrix without breaking audits." },
  { title: "CRM to Cash", role: "SALES · ADMIN", desc: "Lead → Estimate → Proposal → Contract → Deposit, and where authorities gate each step." },
  { title: "Audit & Compliance", role: "OWNER", desc: "Chain integrity, authorised breaks, and how to publish immutable evidence." },
  { title: "Portfolio Manager (Photos + Video)", role: "OWNER · REVIEWER", desc: "Upload → deterministic Pillow / FFmpeg cleanup → GPT-5.4 Vision + Whisper drafts → Founder-only publish. Immutable originals preserved private; reviewers may upload and draft only." },
];

const PHASE3_TUTORIALS = [
  {
    testId: "learn-phase3-portal-accept-invite", title: "Accepting an invitation",
    steps: [
      "Open the account email from ERF and set your password. Then sign in.",
      "Open the Architect Portal link from the second email while signed in. It works once and expires after 72 hours.",
      "If it has expired or was withdrawn, ask ERF to invite you again.",
    ],
  },
  {
    testId: "learn-phase3-portal-portal-login", title: "Logging into the Architect Portal",
    steps: [
      "Sign in at the normal ERF ConstructionOS login page with your email and password.",
      "Choose Architect Portal in the menu.",
    ],
  },
  {
    testId: "learn-phase3-portal-assigned-projects", title: "Viewing assigned projects",
    steps: [
      "The portal shows only projects ERF has assigned to you.",
      "Each card shows drawing packages, revision requests, RFIs awaiting an answer, pending revisions, items awaiting ERF and unread messages.",
    ],
  },
  {
    testId: "learn-phase3-portal-upload-stamped", title: "Uploading stamped drawings",
    steps: [
      "On a project card tap Submissions, enter a title and drawing number, then tap New submission.",
      "Open it and upload a PDF or image (up to 100 MB). Executables and archives are refused.",
      "Your PDF is stored byte-for-byte and never altered. Images get a separate preview copy with location data removed, labelled as a preview.",
      "ConstructionOS does not check, apply or validate professional seals. Uploading a stamped drawing is not a license check or approval.",
    ],
  },
  {
    testId: "learn-phase3-portal-submit-revised", title: "Submitting revised plans",
    steps: [
      "Tap Submit to ERF when the drawing is ready.",
      "When ERF requests a revision, open the submission and tap Upload revised drawing. The earlier revision is kept and marked superseded.",
    ],
  },
  {
    testId: "learn-phase3-portal-respond-rfis", title: "Responding to RFIs",
    steps: [
      "Open Documents and RFIs from a project card to answer open RFIs.",
      "To answer with a drawing, create a submission that references the RFI.",
    ],
  },
  {
    testId: "learn-phase3-portal-communicate", title: "Communicating with ERF project managers",
    steps: [
      "Tap Messages on a project card to read and post in the project conversation.",
      "Opening the conversation marks it read. Email alerts are off unless ERF turns them on.",
    ],
  },
  {
    testId: "learn-phase3-portal-corrections", title: "Responding to correction requests",
    steps: [
      "Correction requests from ERF appear in the submission's Responses and history.",
      "Type your reply and tap Post response, then upload the revised drawing.",
    ],
  },
  {
    testId: "learn-phase3-portal-status", title: "Checking submission status",
    steps: [
      "Statuses are ERF internal document-management steps only: Submitted, ERF internal review, revision requested, acknowledged, accepted for ERF project use, superseded.",
      "They are not governmental approval, a professional seal or legal certification. Only ERF can accept or supersede, and you can never approve your own submission.",
      "Moving a submission backward needs a reason of at least 10 characters.",
    ],
  },
  {
    testId: "learn-phase3-portal-download", title: "Downloading authorized project documents",
    steps: [
      "Tap View original or View preview on a revision. The download link works for 5 minutes.",
      "Every file is checked against its SHA-256 fingerprint before it is served.",
    ],
  },
  {
    testId: "learn-phase3-start", title: "Architect Workspace — getting started",
    steps: [
      "Open Architect Workspace from the left menu.",
      "Pick a project at the top. Architects only see projects they were assigned to.",
      "Founder or Administrator: type the architect's email under Architects on this project and tap Assign. The person must already have the Architect role in People & Access.",
      "Read the yellow notice: this workspace supports drafting and internal review. It does not produce licensed professional seals or code-compliance certifications.",
    ],
  },
  {
    testId: "learn-phase3-revisions", title: "Add a drawing and upload revisions (Founder and Administrator)",
    steps: [
      "Type a drawing title, choose the discipline, add a sheet number (for example A-101) and tap Add drawing.",
      "Open the drawing card, then tap Upload revision. PDF, JPEG, PNG, WebP and HEIC files up to 100 MB are accepted.",
      "Each upload gets the next version number (v1, v2, v3…). Earlier versions are never changed or deleted.",
      "Architects review drawings; they do not add drawings or upload revisions.",
      "Tap Open original to view the exact file. Image previews have location and camera data removed.",
      "Treat preview links like short-lived passwords — don't share them. Each link works for 5 minutes.",
    ],
  },
  {
    testId: "learn-phase3-review", title: "Review, approve or request changes",
    steps: [
      "Write a note in Your comment and tap Comment to discuss the latest version.",
      "Tap Request changes with a short description of what must change, or Approve design when it looks right.",
      "Approve design is a design-review decision only. It is not a seal, a signature or permit approval.",
      "Every comment, decision and upload is written to the audit trail and cannot be edited.",
    ],
  },
  {
    testId: "learn-phase3-status", title: "Document status (Founder and Administrator)",
    steps: [
      "On a drawing, choose a status: A Design Concept, B Contractor-Prepared, C Draft Unsealed or D Professional Review.",
      "Status D needs at least one uploaded revision.",
      "Moving a drawing back to an earlier status needs the Founder and a reason of at least 10 characters.",
      "Signed/sealed, permit and construction statuses (E–I) are blocked. The system refuses them because they need a licensed professional's seal.",
    ],
  },
  {
    testId: "learn-phase3-change-orders", title: "Change orders",
    steps: [
      "Founder or Administrator: under Change orders on the workspace, type a title and amount and tap New change order. Each gets the next number (CO-001, CO-002…).",
      "Tap Submit when it is ready for approval.",
      "Change orders at or above the Founder threshold (default $5,000, set in Founder rules) need the Founder. Smaller ones can be approved by an Administrator.",
      "When approved, the change order is added to the project's billing schedule in Financials and the contract sum goes up. Future pay applications can bill it.",
      "To reject, type a reason of at least 10 characters and tap Reject. Deductive (credit) change orders are not available yet.",
    ],
  },
  {
    testId: "learn-phase3-rfis", title: "Requests for Information (RFIs)",
    steps: [
      "Founder or Administrator: under Requests for Information on the workspace, type a subject and a question (at least 10 characters). Add a spec section like 08 71 00 and a due date if you have them, then tap Raise RFI.",
      "Each RFI gets the next number for the project (RFI-0001, RFI-0002…) and goes to the architect.",
      "Architect: open the RFI, type your answer and tap Send answer. The RFI goes back to the PM.",
      "Founder or Administrator: tap Close RFI when the answer is accepted, or Reopen / Void with a reason of at least 10 characters.",
      "On a phone the RFI list stacks into one column; every action button stays full height for easy tapping.",
    ],
  },
  {
    testId: "learn-phase3-submittals", title: "Submittals",
    steps: [
      "Founder or Administrator: under Submittals, type a title and spec section (for example 08 41 13), tap Add submittal, then Send for review.",
      "The architect picks Approved, Approved as noted, Revise and resubmit, or Rejected. Anything other than Approved needs comments of at least 10 characters.",
      "For Revise and resubmit, type the reason and tap Resubmit. The revision number goes up and every decision is kept in the history and audit log.",
      "A review decision is not a professional seal or stamp.",
    ],
  },
  {
    testId: "learn-phase3-schedule", title: "Project schedule",
    steps: [
      "Under Schedule, add tasks (start and finish) or milestones (one date).",
      "Link activities: pick an activity, what it depends on, the link type (Finish → Start, Start → Start, Finish → Finish, Start → Finish) and any lag in days. Loops are refused.",
      "Links whose dates conflict are shown in red. Nothing is rescheduled automatically and there is no critical-path calculation.",
      "Changing planned dates needs a reason of at least 10 characters and is kept as a revision.",
    ],
  },
  {
    testId: "learn-phase3-procurement", title: "Vendors and purchasing",
    steps: [
      "Add each vendor with their insurance (COI) expiry date and whether a W-9 is on file or they get a 1099. Tax details are tracked only.",
      "A vendor whose insurance is missing or expired is Blocked and cannot be put on a purchase order.",
      "Request material, choose a vendor and amount, then tap Create PO. Enter the quantity received and tap Confirm receipt; partial deliveries are tracked.",
      "Equipment reservations refuse overlapping dates for the same equipment, and usage hours are logged against the reservation.",
    ],
  },
];

const PHASE4_TUTORIALS = [
  {
    testId: "learn-phase4-daily", title: "Daily reports",
    steps: [
      "Open Field Operations from the left menu and tap your project.",
      "Superintendent, Field Supervisor, Founder or Administrator: fill in the date, weather (typed by hand), crew and work performed, then tap Save draft report. One report per project per day.",
      "Open the report card and tap Submit report when the day is done.",
      "Project Manager, Founder or Administrator: open a submitted report and tap Acknowledge. Acknowledged reports are locked.",
      "Need to fix a locked report? Add a correction of at least 10 characters. Corrections are kept permanently and cannot be edited.",
    ],
  },
  {
    testId: "learn-phase4-punch", title: "Punch list",
    steps: [
      "Under Punch list, type the item and its location, then tap Add item. Items are numbered PL-001, PL-002… for each project.",
      "When the work is fixed, tap Ready for inspection.",
      "Project Manager, Founder or Administrator: tap Close after checking it, or type a reason (at least 10 characters) and tap Reopen.",
      "Automatic weather and photo attachments are not available yet.",
    ],
  },
  {
    testId: "learn-phase4-safety", title: "Safety observations and incidents",
    steps: [
      "Under Safety, pick Observation or Incident and a severity, describe what happened, add witness names separated by commas, and tap Report. Items are numbered SAF-001, SAF-002…",
      "Type who will fix it, the due date and the corrective action, then tap Assign fix.",
      "Project Manager, Founder or Administrator: type how it was resolved (at least 10 characters) and tap Close. Every step is kept in the audit log.",
    ],
  },
  {
    testId: "learn-phase4-photos", title: "Site photos and the compliance binder",
    steps: [
      "Under Site photos, add a caption, optionally pick a daily log, then tap Add photo. Location and camera details are removed from every viewable copy; the original stays private.",
      "Under Compliance binder, add permits, inspections and certifications with their expiry dates. Expired items show in red; items expiring within 30 days are flagged.",
      "Tap Check expiries to create in-app reminders. Nothing is emailed or texted.",
    ],
  },
];

const PHASE5_TUTORIALS = [
  {
    testId: "learn-phase5-sov", title: "Contract and schedule of values",
    steps: [
      "Open Financials from the left menu and tap your project.",
      "Founder or Bookkeeper: type the contract sum and tap Create contract. Retention starts at 0%; only the Founder can change it.",
      "Add schedule-of-values lines until the total equals the contract sum exactly. Lines lock once a pay application exists.",
      "Administrators can view everything here but cannot change it.",
    ],
  },
  {
    testId: "learn-phase5-payapp", title: "Pay applications",
    steps: [
      "Tap New pay application, pick the period end date, and enter work done this period and stored materials for each line. Tap Save amounts.",
      "A line cannot be billed past its scheduled value. Retention and current payment due are calculated for you.",
      "Tap Submit for Founder approval. The Founder taps Approve, or Return to draft.",
      "After approval, tap Issue to lock it, post it to the ledger and create the PDF. Nothing is sent and no money moves.",
    ],
  },
  {
    testId: "learn-phase5-ap", title: "Vendor bills and job cost",
    steps: [
      "Under Bills from vendors, enter the vendor, bill number, date, what it was for, a cost code and the amount, then tap Add bill.",
      "Tap Submit. Bills at or above the Founder threshold (default $1,000, set in Founder rules) need the Founder; smaller ones can be approved by the Bookkeeper.",
      "Approved bills are draft entries only. Tap Mark entered for manual posting after you enter it in your accounting system yourself. Nothing is paid.",
      "Add budget lines by cost code to see budget vs actual and the variance. Approved change orders also appear on the billing schedule.",
    ],
  },
  {
    testId: "learn-phase5-waivers", title: "Lien waivers",
    steps: [
      "Under Lien waivers, pick conditional or unconditional, progress or final, enter who it is from, the amount and the through date, then tap Request waiver.",
      "Attach the signed PDF, then tap Mark received. Void needs a reason of at least 10 characters.",
      "Waivers and stored state templates are drafts for review — not legal advice. A licensed attorney must review any lien waiver before use.",
    ],
  },
];

const PHASE6_TUTORIALS = [
  {
    testId: "learn-phase6-access", title: "Give a client access to a project",
    steps: [
      "First invite the person from Users with the Client role. They set their own password from the invitation.",
      "Open Client Sharing from the left menu and tap the project.",
      "Type the client's email and tap Give access. Tap Remove access to take it away; they lose access immediately.",
      "A client only ever sees projects you gave them access to.",
    ],
  },
  {
    testId: "learn-phase6-share", title: "Choose what a client sees",
    steps: [
      "Nothing is visible to clients until you share it. Until then they see “Project onboarding”.",
      "Under Ready to share, tap Share next to an issued pay application or a design-review-approved drawing (status A–D).",
      "If a drawing loses its approval later, clients stop seeing it automatically.",
      "Tap Stop sharing to hide an item again.",
    ],
  },
];

const PHASE7_TUTORIALS = [
  {
    testId: "learn-phase7-units", title: "Properties, units and lease records",
    steps: [
      "Open Property Management from the left menu. Type a property name and tap Add property.",
      "Open the property card, type a unit label (for example 101) and tap Add unit.",
      "Tap Record lease on a unit, enter the tenant name, dates, monthly rent and deposit, then tap Save lease record. Leases on the same unit cannot overlap.",
      "These are records only. No rent is collected and no notices are sent.",
    ],
  },
  {
    testId: "learn-phase7-maintenance", title: "Maintenance requests and work orders",
    steps: [
      "Pick the unit, describe what needs fixing and tap Log request.",
      "Tap Triage once you have looked at it. To create a work order, type the vendor or crew name first.",
      "Tap Mark completed when the work is done. The cards at the top show occupancy, the maintenance backlog and leases ending in the next 60 days.",
      "To cancel an open request, type a reason of at least 10 characters and tap Cancel.",
      "Property Managers can do this too, unless the Founder has turned that permission off in Founder rules.",
    ],
  },
];

const PHASE8_TUTORIALS = [
  {
    testId: "learn-phase8-radar", title: "Risk radar",
    steps: [
      "Open Construction IQ from the left menu.",
      "Each project gets a score from fixed rules: overdue RFIs, open punch items, daily reports waiting over 2 days for acknowledgement, and pay applications not yet issued.",
      "Founder: change how much each signal counts (0–10) and tap Save weights. Every signal starts at 1.",
      "Administrators can view the radar but cannot change it.",
    ],
  },
  {
    testId: "learn-phase8-brief", title: "Weekly brief",
    steps: [
      "Founder: tap Create draft brief. It lists every open item with a link to the record it came from.",
      "Read it, then tap Approve for internal use. Nothing is sent to anyone.",
      "Founder: tap Draft AI summary for a short AI-written summary. Every sentence must link to real records in your company, otherwise it is rejected. It stays a draft until you tap Approve AI summary.",
      "Briefs only use your own company's records. Nothing is sent to anyone.",
    ],
  },
];

const PORTFOLIO_TUTORIALS = [
  {
    title: "Uploading project photos and videos",
    testId: "learn-portfolio-upload",
    steps: [
      "Open Portfolio Manager from the left menu and pick a project (or tap New project).",
      "Drag files onto the dashed box, or tap Choose files. Photos: JPEG, PNG or WebP up to 50 MB. Videos: MP4, MOV, M4V or WebM up to 500 MB.",
      "Each file shows Processing, then Ready. Your original is kept private and unchanged; the public copy has location and camera data removed.",
      "If a file shows Failed, tap Retry processing (up to 3 times).",
    ],
  },
  {
    title: "AI drafts and Founder approval",
    testId: "learn-portfolio-ai-drafts",
    steps: [
      "On a Ready file, tap AI drafts, then Generate AI draft.",
      "The draft suggests a caption, a short description and alt text. It is marked \"AI draft — pending Founder approval\" and is never shown publicly on its own.",
      "Edit the wording if needed, then tap Approve and use. Only the Founder can approve.",
    ],
  },
  {
    title: "Inviting a reviewer",
    testId: "learn-portfolio-reviewer",
    steps: [
      "The person needs a workspace account first — add them in People & Access if they don't have one.",
      "On the project page, go to Reviewers, type their email and tap Invite reviewer.",
      "Reviewers see only the projects they're invited to. They can comment, Approve or Request changes — they cannot publish or delete.",
      "Tap Revoke at any time to remove their access.",
    ],
  },
  {
    title: "Approving and publishing a project",
    testId: "learn-portfolio-publish",
    steps: [
      "Tap Send for review. A reviewer approves it or asks for changes.",
      "When it's Approved, tap Publish…, tick the confirmation box and tap Publish now. Only the Founder can publish; nothing is published automatically.",
      "To take it down, tap Unpublish. You can edit and publish again later.",
    ],
  },
];

export default function Learn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";
  const portfolioFeature = usePortfolioFeature(user?.id);
  const phase3 = usePhaseFeature(3, user?.id);
  const phase4 = usePhaseFeature(4, user?.id);
  const phase5 = usePhaseFeature(5, user?.id);
  const phase6 = usePhaseFeature(6, user?.id);
  const phase7 = usePhaseFeature(7, user?.id);
  const phase8 = usePhaseFeature(8, user?.id);

  return (
    <AdminAppShell
      activeNav="help"
      title="Learn Hub"
      subtitle="Tours · cheat sheets · training paths — no fake courses, only what's shipped."
      user={user}
    >
      <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-tour-section">
        <Card className="border-erf-borderCream">
          <CardContent className="p-5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Guided tour</div>
            <div className="font-display font-semibold text-erf-dark text-lg">Owner Command Center walkthrough</div>
            <div className="mt-2 text-sm font-nav text-erf-dark/70">
              A step-by-step tour of every tile on <code className="font-mono text-xs bg-erf-creamSecondary px-1 rounded">/owner</code>.
              Progress is stored per-user; restarting resets your record and re-shows every step.
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={() => navigate("/owner?tour=restart")}
                className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                data-testid="learn-restart-owner-tour"
                disabled={!isOwner}
                aria-disabled={!isOwner}
                aria-label={isOwner ? "Restart the Owner Command Center tour" : "Owner-only tour"}
              >
                <RotateCcw className="w-4 h-4 mr-1.5" aria-hidden="true" />
                {isOwner ? "Restart Owner tour" : "Owner-only tour"}
              </Button>
              <Link to="/help" data-testid="learn-open-help">
                <Button variant="outline" className="border-erf-borderCream">
                  <BookOpen className="w-4 h-4 mr-1.5" aria-hidden="true" /> Open Help Center
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
        <Card className="border-erf-borderCream" data-testid="learn-video-section">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">Video walkthroughs</div>
              <StatusChip status="phase_n" phase="R6" label="Coming in R6" />
            </div>
            <div className="font-display font-semibold text-erf-dark text-lg">Screen-recorded tutorials</div>
            <div className="mt-2 text-sm font-nav text-erf-dark/70">
              Batch-generated screen recordings ship in R6 alongside the polish pass. No placeholder videos in R1c — you'll see them here the moment they exist.
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className="aspect-video rounded bg-erf-creamSecondary border border-dashed border-erf-borderCream flex items-center justify-center"
                  aria-label={`Video placeholder ${n} — coming in R6`}
                  data-testid={`learn-video-placeholder-${n}`}
                >
                  <Play className="w-5 h-5 text-erf-dark/30" aria-hidden="true" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-cheat-sheets">
        {CHEAT_SHEETS.map((sheet) => {
          const Icon = sheet.icon;
          return (
            <Card key={sheet.title} className="border-erf-borderCream" data-testid={`learn-sheet-${sheet.title.replace(/\s+/g, "-").toLowerCase()}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-erf-gold" aria-hidden="true" />
                  <div className="font-display font-semibold text-erf-dark">{sheet.title}</div>
                </div>
                <ul className="space-y-1.5">
                  {sheet.items.map((item, i) => (
                    <li key={i} className="flex items-baseline gap-3">
                      <code className="font-mono text-[11px] bg-erf-creamSecondary px-2 py-0.5 rounded whitespace-nowrap text-erf-dark">
                        {item.keys}
                      </code>
                      <span className="text-sm font-nav text-erf-dark/80">{item.action}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {portfolioFeature?.enabled && (
        <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-portfolio-tutorials">
          {PORTFOLIO_TUTORIALS.map((t) => (
            <Card key={t.testId} className="border-erf-borderCream" data-testid={t.testId}>
              <CardContent className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Portfolio Manager · Beginner</div>
                <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">{t.title}</h2>
                <ol className="mt-2 list-decimal pl-5 space-y-1.5">
                  {t.steps.map((s, i) => <li key={i} className="text-sm font-nav text-erf-dark/80">{s}</li>)}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {phase3?.enabled && ARCHITECT_ROLES.includes(user?.role_key) && (
        <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-phase3-tutorials">
          {PHASE3_TUTORIALS.map((t) => (
            <Card key={t.testId} className="border-erf-borderCream" data-testid={t.testId}>
              <CardContent className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Architect Workspace · Beginner</div>
                <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">{t.title}</h2>
                <ol className="mt-2 list-decimal pl-5 space-y-1.5">
                  {t.steps.map((s, i) => <li key={i} className="text-sm font-nav text-erf-dark/80">{s}</li>)}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {phase4?.enabled && FIELD_ROLES.includes(user?.role_key) && (
        <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-phase4-tutorials">
          {PHASE4_TUTORIALS.map((t) => (
            <Card key={t.testId} className="border-erf-borderCream" data-testid={t.testId}>
              <CardContent className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Field Operations · Beginner</div>
                <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">{t.title}</h2>
                <ol className="mt-2 list-decimal pl-5 space-y-1.5">
                  {t.steps.map((s, i) => <li key={i} className="text-sm font-nav text-erf-dark/80">{s}</li>)}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {phase5?.enabled && FINANCE_ROLES.includes(user?.role_key) && (
        <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-phase5-tutorials">
          {PHASE5_TUTORIALS.map((t) => (
            <Card key={t.testId} className="border-erf-borderCream" data-testid={t.testId}>
              <CardContent className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Financials · Beginner</div>
                <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">{t.title}</h2>
                <ol className="mt-2 list-decimal pl-5 space-y-1.5">
                  {t.steps.map((s, i) => <li key={i} className="text-sm font-nav text-erf-dark/80">{s}</li>)}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {phase6?.enabled && ["OWNER_SUPER_ADMIN", "ADMINISTRATOR"].includes(user?.role_key) && (
        <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-phase6-tutorials">
          {PHASE6_TUTORIALS.map((t) => (
            <Card key={t.testId} className="border-erf-borderCream" data-testid={t.testId}>
              <CardContent className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Client Portal · Beginner</div>
                <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">{t.title}</h2>
                <ol className="mt-2 list-decimal pl-5 space-y-1.5">
                  {t.steps.map((s, i) => <li key={i} className="text-sm font-nav text-erf-dark/80">{s}</li>)}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {phase7?.enabled && ["OWNER_SUPER_ADMIN", "ADMINISTRATOR", "PROPERTY_MANAGER"].includes(user?.role_key) && (
        <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-phase7-tutorials">
          {PHASE7_TUTORIALS.map((t) => (
            <Card key={t.testId} className="border-erf-borderCream" data-testid={t.testId}>
              <CardContent className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Property Management · Beginner</div>
                <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">{t.title}</h2>
                <ol className="mt-2 list-decimal pl-5 space-y-1.5">
                  {t.steps.map((s, i) => <li key={i} className="text-sm font-nav text-erf-dark/80">{s}</li>)}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {phase8?.enabled && ["OWNER_SUPER_ADMIN", "ADMINISTRATOR"].includes(user?.role_key) && (
        <div className="grid gap-4 md:grid-cols-2 mb-6" data-testid="learn-phase8-tutorials">
          {PHASE8_TUTORIALS.map((t) => (
            <Card key={t.testId} className="border-erf-borderCream" data-testid={t.testId}>
              <CardContent className="p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-goldHover mb-1">Construction IQ · Beginner</div>
                <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">{t.title}</h2>
                <ol className="mt-2 list-decimal pl-5 space-y-1.5">
                  {t.steps.map((s, i) => <li key={i} className="text-sm font-nav text-erf-dark/80">{s}</li>)}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="border-erf-borderCream" data-testid="learn-training-paths">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-erf-gold" aria-hidden="true" />
            <div className="font-display font-semibold text-erf-dark">Role-based training paths</div>
            <StatusChip status="phase_n" phase="R6" label="Certifications ship in R6" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {TRAINING_TILES.filter((tile) => portfolioFeature?.enabled || !tile.title.startsWith("Portfolio Manager")).map((tile) => (
              <div
                key={tile.title}
                className="border border-erf-borderCream rounded-md p-3 bg-erf-cream/50"
                data-testid={`learn-track-${tile.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60">{tile.role}</div>
                <div className="mt-1 font-display font-semibold text-erf-dark flex items-center gap-2">
                  <ScrollText className="w-4 h-4 text-erf-dark/60" aria-hidden="true" />
                  {tile.title}
                </div>
                <div className="mt-1 text-sm font-nav text-erf-dark/70">{tile.desc}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </AdminAppShell>
  );
}
